import React, { useEffect, useRef, useState, useCallback, memo } from 'react'

import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import Button from '@space-core/components/button'
import TextField from '@space-core/components/form/textField'
import Icon from '@space-core/components/icon'
import Modal from '@space-core/components/modal'
import ModalBody from '@space-core/components/modalBody'
import ModalFooter from '@space-core/components/modalFooter'
import ModalHeader from '@space-core/components/modalHeader'
import useTranslation from '@space-core/hooks/useTranslation'
import useMenuCommunity from '@space-shell/common/components/customMenu/menuCommunityItem/hooks/useMenuCommunity'
import useMembership from '@space-shell/common/hooks/useMembership'
import type { CommunityGroupCreateResponse } from '@space-shell/common/services/communities'
import clsx from 'clsx'
import styled from 'styled-components'

export type GroupOnSubmitProps = {
  inputValue: string
  groupId: number
}

export type GroupCommunityModalProps = {
  isOpen: boolean
  closeModal: () => void
  onSubmited?: ({ inputValue, groupId }: GroupOnSubmitProps) => void
  group?: CommunityGroupCreateResponse
  isEditing?: boolean
}

const CustomTextField = styled(TextField)`
  background-color: var(--background-color) !important;
`

const InputCounter = styled.div`
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  color: var(--gray-500);
  background-color: var(--dropdown-background-color);

  span {
    color: inherit;
    font-size: inherit;
  }
`

const paddingModalStyle = '_px-5 _py-5'

const MAX_LENGTH_INPUT = 40
const MIN_LENGTH_INPUT = 1

const GroupCommunityModal = ({
  isOpen,
  group,
  closeModal,
  isEditing = false
}: GroupCommunityModalProps) => {
  const [inputValue, setInputValue] = useState<string>(group?.name ?? '')
  const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false)
  const { postGroupMutation, putGroupMutation } = useMenuCommunity()
  const { membershipSlug } = useMembership()
  const inputRef = useRef<HTMLInputElement>(null)

  const { t } = useTranslation()

  useEffect(() => {
    isEditing && group?.name && setInputValue(group.name)
  }, [isEditing, group?.name])

  useEffect(() => {
    inputRef.current && inputRef.current.focus()
  }, [])

  const handleClose = useCallback(() => {
    closeModal()
    setInputValue('')
  }, [closeModal])

  const handleOnSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault()

    if (!inputValue) return null

    setIsLoadingButton(true)

    if (isEditing) {
      await putGroupMutation({
        payload: {
          id: Number(group?.id),
          name: inputValue,
          slug: membershipSlug
        }
      })
    } else {
      await postGroupMutation({
        payload: {
          name: inputValue,
          slug: membershipSlug,
          currentOrder: group?.order
        }
      })
    }

    setIsLoadingButton(false)

    return handleClose()
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target?.value)
  }

  return (
    <Modal show={isOpen} onClose={handleClose}>
      <ModalHeader className='_px-5 _pt-5 _pb-0 '>
        <div className='_pb-1 _d-flex _justify-content-between _align-items-center'>
          <h2 className='_m-0 _text-4 _text-gray-700'>
            {!isEditing ? t('modal_create_group.create_title') : t('modal_create_group.edit_title')}
          </h2>

          <Button
            variation='tertiary'
            className='_p-0 _d-flex _border-0 _bg-transparent  _text-gray-900'
            onClick={handleClose}>
            <span className='_sr-only'>close</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </Button>
        </div>
      </ModalHeader>

      <ModalBody className={paddingModalStyle}>
        <form onSubmit={handleOnSubmit}>
          <div className='hot-form'>
            <label className='hot-form__label _text-gray-900' htmlFor='inputName'>
              {t('modal_create_group.label_name')}
            </label>

            <div className='_position-relative'>
              <InputCounter
                className='_position-absolute _d-flex _flex-row _align-items-center _justify-content-start _mr-3 _text-1'
                data-test='input-value-counter'>
                <span>{inputValue.length}</span>
                <span>/</span>
                <span>{MAX_LENGTH_INPUT}</span>
              </InputCounter>
              <CustomTextField
                ref={inputRef}
                id='inputName'
                name='inputName'
                type='text'
                value={inputValue}
                onChange={handleOnChange}
                className='hot-form__input hot-form__input--md'
                placeholder={t('modal_create_group.placeholder_name')}
                aria-label={t('modal_create_group.placeholder_name')}
                autoComplete='off'
                maxLength={MAX_LENGTH_INPUT}
                min={MIN_LENGTH_INPUT}
              />
            </div>
          </div>
        </form>
      </ModalBody>

      <ModalFooter className={clsx('_align-items-center _justify-content-end', paddingModalStyle)}>
        <Button
          type='button'
          variation='tertiary'
          aria-label={t('buttons.cancel')}
          onClick={handleClose}>
          {t('buttons.cancel')}
        </Button>

        <Button
          loading={isLoadingButton}
          type='submit'
          variation='primary'
          aria-label={!isEditing ? t('modal_create_group.save_button') : t('buttons.edit_group')}
          disabled={!inputValue.trim()}
          onClick={handleOnSubmit}>
          {!isEditing ? t('modal_create_group.save_button') : t('buttons.edit_group')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default memo(GroupCommunityModal)
